/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { ReadmeEditorView } from "../ReadmeEditorView";
import { useStateWithEvent } from "../../../../ui/hooks";
import { CodeEditor } from "@adapptio/lib-editor-ui";
import { isSelectionItemPage } from "../../Common/ReadmeEditSelection";
import { TGetBlueprintSchemaModel } from "@adapptio/lib-blueprint";
import { TReadmePageSchema } from "@adapptio/lib-core";
import { Panel, PANEL_STYLE, PanelNote } from "../../../../ui/components/Panel";

export interface IReadmeEditModeProps {
	view: ReadmeEditorView;
}

export const ReadmeEditMode: FC<IReadmeEditModeProps> = ({ view }) => {
	const baseClass = BEM("edt-readme-edit-mode");
	const model = view.getModel();
	const selection = useMemo(() => model.getSelection(), [ model ]);

	const getSelectedPage = (): TGetBlueprintSchemaModel<TReadmePageSchema> | null => {
		const items = selection.getItems();
		if (items.length === 0) {
			return null;
		}
		const selectedItem = items[0];
		if (!isSelectionItemPage(selectedItem)) {
			return null;
		}
		const pagesModel = model.getModel()?.props.spec.props.pages;
		if (!pagesModel) {
			return null;
		}
		return pagesModel.items.find((item) => item.nodeId === selectedItem.nodeId) || null;
	};

	const selectedPage = useStateWithEvent(
		selection.onChange,
		() => getSelectedPage(),
		[ model, selection ]
	);

	// Also update when model changes
	useStateWithEvent(
		model.onModelChange,
		() => getSelectedPage(),
		[ model, selection ]
	);

	const content = selectedPage?.props.content.value || "";

	const handleChange = useCallback(
		(newContent: string) => {
			const page = getSelectedPage();
			if (page) {
				page.props.content.schema.setValue(page.props.content, newContent, true);
			}
		},
		[ model, selection ]
	);

	if (!selectedPage) {
		return (
			<Panel style={PANEL_STYLE.NONE} className={baseClass.toClassName()}>
				<PanelNote align="center">
					No page selected. Add a page using the + button in the sidebar.
				</PanelNote>
			</Panel>
		);
	}

	return (
		<Panel style={PANEL_STYLE.NONE} scroll={false} className={baseClass.toClassName()}>
			<CodeEditor
				value={content}
				onChange={handleChange}
				language="markdown"
				options={{
					lineNumbers: "on",
					minimap: { enabled: false },
					wordWrap: "on",
					wrappingIndent: "same"
				}}
			/>
		</Panel>
	);
};
